// Catalog.ID Client — Unlock Requests Pane
// ============================================================================
// For operator sessions only. Shows accounts with pending unlock requests.
// Operators can approve unlocks to allow key rotation after lockdown.

import {
    createListPaneType,
    createElement,
    createFieldGroup,
    createFormRow,
    createEmptyState,
    renderPaneActions,
} from '@catalog-shell';
import type {
    ListPaneConfig, PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as accountApi from '../api/account';
import type { PendingUnlockAccount } from '../api/types';
import { PANE_IDS } from '../shell-config';
import { formatApiError } from '../api/client';

// ============================================================================
// Unlock Requests List
// ============================================================================

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

const columns: ColumnDef<PendingUnlockAccount>[] = [
    {
        key: 'cid',
        header: 'Catalog.ID',
        width: 160,
        getValue: (item) => item.cid,
        type: { kind: 'text' },
    },
    {
        key: 'accountType',
        header: 'Identity',
        width: 100,
        getValue: (item) => item.accountType,
        type: {
            kind: 'text',
            converter: (v) => ACCOUNT_TYPE_LABELS[v as string] ?? String(v),
        },
    },
    {
        key: 'lockedAt',
        header: 'Locked',
        width: 140,
        getValue: (item) => item.lockedAt,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleString(); }
                catch { return String(v); }
            },
        },
    },
];

function createUnlockDataSource(): DataSource<PendingUnlockAccount> {
    return new DataSource<PendingUnlockAccount>({
        fetch: async (params): Promise<FetchResult<PendingUnlockAccount>> => {
            const accounts = await accountApi.getPendingUnlocks();
            const sliced = accounts.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: accounts.length };
        },
        keyFn: (item) => item.cid,
        pageSize: 100,
    });
}

const listConfig: ListPaneConfig<PendingUnlockAccount> = {
    typeId: PANE_IDS.unlockRequests,
    defaultTitle: 'Unlock Requests',
    createDataSource: () => createUnlockDataSource(),
    columns,
    keyFn: (item) => item.cid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'unlocks') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.unlockRequestDetail,
            title: `Unlock: ${item.cid}`,
            context: { unlockAccount: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    ariaLabel: 'Pending unlock requests',
};

export function createUnlockRequestsPane() {
    return createListPaneType(listConfig);
}

// ============================================================================
// Unlock Request Detail
// ============================================================================

// ----------------------------------------------------------------------------
// Title-row action
//
// Approving the unlock is this pane's whole point — one verb about the request
// the pane is showing — so it rides in the title row (shell USAGE, "Where a
// button belongs") and the body is left as pure evidence: who is locked out,
// and how to verify them before letting them back in.
// ----------------------------------------------------------------------------

/** What the title row needs to know about the request on screen. */
export interface UnlockDetailActionState {
    account: PendingUnlockAccount | null;
    /** The approval POST is in flight — a second click would re-send it. */
    busy: boolean;
}

interface UnlockDetailState extends UnlockDetailActionState {
    ctx: PaneTypeRenderContext;
}

const states = new WeakMap<PaneInstance, UnlockDetailState>();

/** Pure: the single verb, and whether it is currently available. */
export function planUnlockDetailActions(
    st: UnlockDetailActionState | undefined,
    exec: () => void,
): PaneActionDescriptor[] {
    if (!st?.account) return [];
    return [{
        key: 'approve',
        label: 'Approve Unlock',
        variant: 'primary',
        disabled: st.busy,
        title: st.busy ? 'Approving this unlock…' : undefined,
        execute: () => exec(),
    }];
}

function unlockDetailActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planUnlockDetailActions(st, () => { void approveUnlock(st!); }));
}

async function approveUnlock(st: UnlockDetailState): Promise<void> {
    const { ctx, account } = st;
    if (!account || st.busy) return;
    st.busy = true;
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    try {
        await accountApi.approveUnlock({ cid: account.cid });
        ctx.shell.emit('data-changed', { type: 'unlocks' });
        ctx.shell.toast(`Unlock approved for ${account.cid}`, 'success');
        const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
        surface?.closePane(ctx.instance.instanceId);
    } catch (err) {
        ctx.shell.toast(formatApiError(err, 'Approval failed'), 'error');
        st.busy = false;
        ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    }
}

export function createUnlockRequestDetailPane(): PaneType {
    return {
        typeId: PANE_IDS.unlockRequestDetail,
        kind: 'leaf',
        defaultTitle: 'Unlock Request',
        capabilities: [],
        tabBarActions: unlockDetailActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const account = ctx.instance.context['unlockAccount'] as PendingUnlockAccount | undefined;
            states.set(ctx.instance, { ctx, account: account ?? null, busy: false });

            if (!account) {
                container.appendChild(createEmptyState({ message: 'No request selected' }));
                return container;
            }

            renderUnlockDetail(container, account);
            // The title row was built before this render; plan it now that the
            // pane knows which request it is showing.
            queueMicrotask(() => ctx.shell.refreshPaneActions(ctx.instance.instanceId));
            return container;
        },
    };
}

function renderUnlockDetail(container: HTMLElement, account: PendingUnlockAccount): void {
    // Info section
    const infoGroup = createFieldGroup({ title: 'Account Information' });

    const cidRow = createFormRow({ label: 'Catalog.ID' });
    cidRow.content.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: account.cid,
    }));
    infoGroup.body.appendChild(cidRow.element);

    const typeRow = createFormRow({ label: 'Account Type' });
    typeRow.content.appendChild(createElement('span', {
        textContent: ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType,
    }));
    infoGroup.body.appendChild(typeRow.element);

    const lockedRow = createFormRow({ label: 'Locked At' });
    lockedRow.content.appendChild(createElement('span', {
        textContent: account.lockedAt ? new Date(account.lockedAt).toLocaleString() : 'Unknown',
    }));
    infoGroup.body.appendChild(lockedRow.element);

    container.appendChild(infoGroup.element);

    // Instructions
    const instructionsGroup = createFieldGroup({ title: 'Verification' });
    const instructionsRow = createFormRow({ label: '' });
    instructionsRow.content.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Before approving, verify the account holder\'s identity through an independent validated channel (email, phone, or document verification). Approving allows the account holder to log in and rotate their keys.',
    }));
    instructionsGroup.body.appendChild(instructionsRow.element);
    container.appendChild(instructionsGroup.element);
}
